"use client";

import { getAuthToken } from "@/components/auth-provider";
import { securePost, secureFetch } from "@/lib/security/fetch-client";
import {
  getShanghaiDateContext,
  normalizeKeywordCountForTask,
} from "@/lib/agent/task-context";
import { z } from "zod";

/**
 * 客户端 Page Agent 桥接。
 *
 * 真正驱动 Page Agent：先向 /api/agent/conversations 扣额并取得任务凭证，
 * 再用凭证运行 agent.execute(task)，期间把 activity 事件回传给 UI，
 * 结束后 PATCH 任务状态（失败则服务端退还额度）。
 *
 * 使用 PageAgentCore + PageController（不带内置 Panel），由自定义 React UI
 * 订阅 agent 事件并驱动 execute，实现 OStudio 风格界面。
 * 内置 Panel 会在 status=running 时强制 show 自己，无法靠 hide 抑制，
 * 所以必须用 Core 直接组装，彻底不引入 Panel。
 */

type PageAgentModule = typeof import("page-agent");
type AgentInstance = InstanceType<PageAgentModule["PageAgentCore"]>;

export class AgentQuotaExhaustedError extends Error {
  constructor() {
    super("QUOTA_EXHAUSTED");
    this.name = "AgentQuotaExhaustedError";
  }
}

let agentPromise: Promise<AgentInstance> | null = null;
let currentTaskToken: string | null = null;
let currentTaskId: string | null = null;
let currentTaskText = "";
let lastQuota: { remaining: number; limit: number } | null = null;
const quotaListeners = new Set<(remaining: number) => void>();

/**
 * 跨轮对话历史摘要。
 *
 * page-agent 的 execute(task) 每次都会 this.history = [] 清空单轮内 step 历史，
 * 即每个 task 是独立的 agent run，不天然保留多轮上下文。为支持"基于此前内容追问"
 * （如先问"C罗今年上热搜次数"再问"那他哪天上的"），这里手动维护 user/assistant 摘要，
 * 由 getPageInstructions 注入到每步的 page 指令中。仅保留最近若干轮，控制 token。
 */
interface ConversationTurn {
  user: string;
  assistant: string;
}
const conversationTurns: ConversationTurn[] = [];
const MAX_CONTEXT_TURNS = 4;

/** 追加一轮对话（execute 完成后由 onAfterTask 调用）。 */
function appendConversationTurn(user: string, assistant: string): void {
  const u = user.trim();
  const a = assistant.trim();
  if (!u || !a) return;
  conversationTurns.push({ user: u, assistant: a });
  while (conversationTurns.length > MAX_CONTEXT_TURNS) conversationTurns.shift();
}

/** 重置对话历史（新会话/面板关闭重开时调用）。 */
export function resetConversationHistory(): void {
  conversationTurns.length = 0;
}

/** 组装历史上下文片段，供 getPageInstructions 注入。无历史返回空串。 */
function conversationContextBlock(): string {
  if (conversationTurns.length === 0) return "";
  const lines = conversationTurns.map(
    (t) => `用户：${t.user}\n助手：${t.assistant}`
  );
  return [
    "",
    "【本会话之前的对话】用户可能基于此前内容追问，请结合上下文理解本轮意图：",
    ...lines,
  ].join("\n");
}

/** 通用工具执行模式：申请grant → 调用工具API → 返回JSON字符串 */
async function executeToolCall(toolName: string, apiPath: string, input: unknown): Promise<string> {
  const grantResp = await securePost<{ grant: string }>("/api/agent/grant", {
    toolName,
    input,
  });
  const result = await securePost(`/api/agent/tools/${apiPath}`, {
    grant: grantResp.grant,
    input,
  });
  return JSON.stringify(result);
}

async function getAgent(): Promise<AgentInstance> {
  if (!agentPromise) {
    agentPromise = initAgent();
  }
  return agentPromise;
}

/**
 * 获取 agent 单例（懒加载）。自定义 UI 通过它订阅事件并调用 execute。
 * 无内置 Panel，完全由自定义 UI 驱动。
 */
export async function getAgentCore(): Promise<AgentInstance> {
  return getAgent();
}

/**
 * 订阅配额变化（任务扣额后触发）。
 */
export function onQuotaChange(fn: (remaining: number) => void): () => void {
  quotaListeners.add(fn);
  if (lastQuota) fn(lastQuota.remaining);
  return () => quotaListeners.delete(fn);
}

async function initAgent(): Promise<AgentInstance> {
  const module: PageAgentModule = await import("page-agent");
  const { PageAgentCore, tool } = module;
  const { PageController } = await import("@page-agent/page-controller");
  const currentWindow = window as Window & { pageAgentCore?: AgentInstance };

  if (currentWindow.pageAgentCore) {
    return currentWindow.pageAgentCore;
  }

  // 用真实模型名，让 page-agent 的 modelPatch 能按模型族做适配
  // （如 DeepSeek 需移除 tool_choice）。模型名非密钥，密钥仍在服务端网关。
  const model = process.env.NEXT_PUBLIC_AGENT_MODEL || "qwen3.7-max";

  // PageController 开启 AI 光标可视化：操作时显示彩色鼠标图样、移动轨迹和点击涟漪。
  // enableMask=true 才会初始化 SimulatorMask（AI 光标 + 点击动画由它承载）。
  // 全网格编号标签保持关闭——交互式助手不需要在页面上铺满程序员风格的元素线框。
  const pageController = new PageController({
    enableMask: true,
    highlightOpacity: 0,
    highlightLabelOpacity: 0,
  });

  // 彩虹遮罩/鼠标只在「真正执行页面操作」时显示。
  // PageAgentCore 默认在每个步骤开头都 showMask()（含思考、数据查询），会把彩虹动画
  // 铺满整步。这里覆写 showMask 为 no-op，改为由下面的 activity 事件精确控制：
  // 仅当 executing 的是页面操作工具（click_element_by_index / scroll）时才显示光标，
  // executed / thinking / error 时隐藏。这样不修改外部组件，只在自有实例上接管可见性。
  // hideMask 保留原实现，确保任务结束时(core finally)仍能隐藏。
  const pageOpTools = new Set(["click_element_by_index", "scroll"]);
  const realShowMask = pageController.showMask.bind(pageController);
  const realHideMask = pageController.hideMask.bind(pageController);
  const showCursorMask = () => {
    // SimulatorMask 已在构造时异步初始化；任务执行时必然就绪，直接 show。
    void realShowMask();
  };
  const hideCursorMask = () => {
    void realHideMask();
  };
  pageController.showMask = async () => {
    /* no-op：见上方说明，可见性由 activity 事件接管 */
  };

  const agent = new PageAgentCore({
    pageController,
    baseURL: "/api/agent/llm",
    apiKey: "server-controlled", // 网关忽略客户端 key，服务端注入真 key
    model,
    language: "zh-CN",
    maxSteps: 6,
    stepDelay: 0.25,
    customFetch: async (input, init) => {
      const headers = new Headers(init?.headers);
      const token = getAuthToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      if (currentTaskToken) {
        headers.set("X-Agent-Task", currentTaskToken);
      }
      return fetch(input, { ...init, headers });
    },
    customTools: {
      input_text: null,
      select_dropdown_option: null,
      scroll_horizontally: null,
      execute_javascript: null,
      // ─── Tool 1: daily_hotlist ───
      daily_hotlist: tool({
        description: "查询某一天的热搜记录，只返回该日最新快照和最多 50 条热搜结果。",
        inputSchema: z.object({
          date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          limit: z.number().int().min(1).max(50).default(20),
        }),
        execute: async function executeDailyHotlist(input) {
          return executeToolCall("daily_hotlist", "daily-hotlist", input);
        },
      }),
      // ─── Tool 2: keyword_count（改造：支持日期范围） ───
      keyword_count: tool({
        description: "统计某个关键词上过几次热搜。支持整年(year)或自定义日期范围(startDate/endDate，不超过365天)。返回 distinctTopicCount（上榜的独立词条数，即「上了几次热搜」的正确答案）和 snapshotRecordCount（采集快照记录数，不等于上热搜次数）。回答用户「上了几次热搜/上过多少次」时必须用 distinctTopicCount，不要用 snapshotRecordCount。",
        inputSchema: z.object({
          keyword: z.string().trim().min(1).max(50),
          year: z.number().int().min(2000).max(2100).optional(),
          startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
          endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
        }),
        execute: async function executeKeywordCount(input) {
          const normalizedInput = normalizeKeywordCountForTask(
            input,
            currentTaskText
          );
          return executeToolCall("keyword_count", "keyword-count", normalizedInput);
        },
      }),
      // ─── Tool 3: keyword_trend ───
      keyword_trend: tool({
        description: "查询关键词最近N天的每日热度趋势，返回每天的峰值热度、平均排名和出现次数，以及趋势方向判断(上升/下降/稳定)。",
        inputSchema: z.object({
          keyword: z.string().trim().min(1).max(50),
          days: z.number().int().min(1).max(90).default(7),
        }),
        execute: async function executeKeywordTrend(input) {
          return executeToolCall("keyword_trend", "keyword-trend", input);
        },
      }),
      // ─── Tool 4: topic_search ───
      topic_search: tool({
        description: "搜索热搜词条，支持时间范围过滤。返回匹配的词条列表（最多20条），含上榜次数、最高热度等信息。",
        inputSchema: z.object({
          keyword: z.string().trim().min(1).max(50),
          limit: z.number().int().min(1).max(20).default(10),
          startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
          endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
        }),
        execute: async function executeTopicSearch(input) {
          return executeToolCall("topic_search", "topic-search", input);
        },
      }),
      // ─── Tool 5: topic_detail ───
      topic_detail: tool({
        description: "查询单个词条的详情：上榜次数、最高热度、最高排名、在榜时长、最近排名历史（最多100条）和关联词条（Top5）。",
        inputSchema: z.object({
          keyword: z.string().trim().min(1).max(50),
        }),
        execute: async function executeTopicDetail(input) {
          return executeToolCall("topic_detail", "topic-detail", input);
        },
      }),
      // ─── Tool 6: topic_compare ───
      topic_compare: tool({
        description: "对比2-5个词条在最近N天内的热度。返回每个词条的汇总统计和每日对比数据。",
        inputSchema: z.object({
          keywords: z.array(z.string().trim().min(1).max(50)).min(2).max(5),
          days: z.number().int().min(1).max(30).default(7),
        }),
        execute: async function executeTopicCompare(input) {
          return executeToolCall("topic_compare", "topic-compare", input);
        },
      }),
      // ─── Tool 7: ranking_snapshot ───
      ranking_snapshot: tool({
        description: "查看当前动态榜单：上升最快(rising)、新上榜(new)、下降最快(falling)或稳定榜(stable)。默认查询上升最快。",
        inputSchema: z.object({
          type: z.enum(["rising", "new", "falling", "stable"]).default("rising"),
          limit: z.number().int().min(1).max(20).default(10),
          hours: z.number().int().min(1).max(168).default(24),
        }),
        execute: async function executeRankingSnapshot(input) {
          return executeToolCall("ranking_snapshot", "ranking-snapshot", input);
        },
      }),
      // ─── Tool 8: celebrity_ranking ───
      celebrity_ranking: tool({
        description: "查询最近N小时内明星热搜排行，可按类别筛选。返回明星姓名、上榜次数、最高热度等。",
        inputSchema: z.object({
          hours: z.number().int().min(1).max(720).default(168),
          category: z.enum(["演员", "歌手", "运动员", "导演", "综艺"]).optional(),
          limit: z.number().int().min(1).max(20).default(10),
        }),
        execute: async function executeCelebrityRanking(input) {
          return executeToolCall("celebrity_ranking", "celebrity-ranking", input);
        },
      }),
      // ─── Tool 9: entity_ranking ───
      entity_ranking: tool({
        description: "查询某个城市或国家近30天的热搜排行。数据来自预聚合缓存，返回关联的热搜词条和热度趋势。",
        inputSchema: z.object({
          entityType: z.enum(["country", "city"]),
          entityName: z.string().trim().min(1).max(50),
          limit: z.number().int().min(1).max(20).default(10),
        }),
        execute: async function executeEntityRanking(input) {
          return executeToolCall("entity_ranking", "entity-ranking", input);
        },
      }),
      // ─── Tool 10: time_distribution ───
      time_distribution: tool({
        description: "查询热搜的时段分布规律：按小时(hourly)、按星期(weekly)或按月(monthly)。返回各时段的上榜次数和平均热度。",
        inputSchema: z.object({
          type: z.enum(["hourly", "weekly", "monthly"]).default("hourly"),
          year: z.number().int().min(2020).max(2100).optional(),
        }),
        execute: async function executeTimeDistribution(input) {
          return executeToolCall("time_distribution", "time-distribution", input);
        },
      }),
      // ─── Tool 11: subscription_confirm ───
      subscription_confirm: tool({
        description: "在用户明确确认后订阅一个关键词词条。",
        inputSchema: z.object({
          keyword: z.string().trim().min(1).max(50),
        }),
        execute: async function executeSubscriptionConfirm(input) {
          return executeToolCall("subscription_confirm", "subscription-confirm", input);
        },
      }),
    },
    instructions: {
      system: [
        "你是微博热搜时光机的安全受控助手。",
        "只使用已开放的工具，不要执行脚本、不要输入任意文本、不要横向滚动、不要尝试绕过权限。",
        "遇到需要用户确认的内容时优先 ask_user。",
        "所有结果要用中文，简洁、专业、可执行。",
        "",
        "工具使用规则（必须严格遵守）：",
        "- 查询某一天的热搜 → daily_hotlist(date)",
        "- 统计上榜次数（整年）→ keyword_count(keyword, year)",
        "- 统计上榜次数（自定义范围，如上周/本月）→ keyword_count(keyword, startDate, endDate)。startDate 和 endDate 必须同时提供，范围不超过365天。",
        "- 「上了几次热搜/上过多少次」用 keyword_count 返回的 distinctTopicCount（独立词条数）作答；snapshotRecordCount 是采集快照记录数，不是上热搜次数，不得作为次数答案。",
        "- 查热度趋势 → keyword_trend(keyword, days)",
        "- 搜索词条 → topic_search(keyword)",
        "- 查词条详情/最高排名/关联词条 → topic_detail(keyword)",
        "- 对比多个词条 → topic_compare(keywords, days)",
        "- 查当前动态（上升最快/新上榜/下降最快/稳定榜）→ ranking_snapshot(type)",
        "- 查明星排行 → celebrity_ranking(hours, category?)",
        "- 查城市/国家热搜 → entity_ranking(entityType, entityName)",
        "- 查时段分布 → time_distribution(type, year?)",
        "- 订阅关键词 → subscription_confirm(keyword)",
        "",
        "时间语义规则：",
        '- "今天/昨天" → 基于当前上海日期计算',
        '- "上周" → 当前日期往前7-14天的自然周（周一至周日）',
        '- "本月" → 当前月份1号至月末',
        '- "最近N天" → 当前日期往前推N天',
        '- "今年/本年" → 当前年份',
        "",
        "- 工具返回真实数据后，再用 done 总结结果。在调用对应工具之前，禁止用 done 结束任务。",
        "- 只有在用户闲聊、问站点功能等无需数据查询时，才可不调用工具直接 done 回答。",
        "",
        "页面操作优先规则（重要）：",
        "- 当用户意图是「打开/打开看/跳转/去/查看」某个页面或榜单（如明星榜、城市榜、词云、日榜、对比页、历史等），且当前页面存在可点击的导航入口时，必须用 click_element_by_index 点击对应导航链接完成页面跳转，不要用数据查询工具代替。",
        "- 只有用户想要具体数据、数字、列表、排行内容时，才用查询工具（celebrity_ranking/entity_ranking 等）。",
        "- 区分「打开页面」与「查询数据」：『打开明星榜单』= 点击导航进入明星页；『最近哪些明星上热搜』= 调用 celebrity_ranking。",
      ].join("\n"),
      getPageInstructions: () => {
        const context = getShanghaiDateContext();
        return [
          "当前页面是热搜时光机站点。优先帮助用户完成查询、回看和订阅相关任务。",
          `当前上海日期是 ${context.date}，当前年份是 ${context.year}。`,
          "用户说「今天、昨天、今年、本年」时，必须基于这个上海日期计算，禁止使用模型训练时间。",
          conversationContextBlock(),
        ].join("\n");
      },
    },
    // 任务生命周期钩子：原生 Panel 触发 agent.execute(task) 时，
    // onBeforeTask 扣减一次额度并签发任务凭证，供 customFetch 注入 X-Agent-Task；
    // 扣额失败则抛错中止任务。这保证"一轮对话扣一次"且不可绕过。
    onBeforeTask: async (agent) => {
      const task = agent.task;
      if (!task) throw new Error("任务为空");
      let conversation: ConversationStart;
      try {
        conversation = await securePost<ConversationStart>("/api/agent/conversations", { task });
      } catch (error) {
        const msg = error instanceof Error ? error.message : "";
        if (msg.includes("429")) throw new AgentQuotaExhaustedError();
        throw error;
      }
      currentTaskText = task;
      currentTaskToken = conversation.token;
      currentTaskId = conversation.taskId;
      lastQuota = { remaining: conversation.remaining, limit: 50 };
      quotaListeners.forEach((fn) => fn(conversation.remaining));
    },
    onAfterTask: async (agent, result) => {
      const taskId = currentTaskId;
      // 暂存本轮用户原文，供下方累积对话历史（currentTaskText 随即清空）。
      const taskText = currentTaskText;
      // 区分"用户主动停止"与"平台/供应商失败"：停止不退额度，失败才退。
      // agent.status 在停止后为 "stopped"；result.success=false 但属于停止。
      const isStopped = agent.status === "stopped";
      const status: "completed" | "failed" | "stopped" = isStopped
        ? "stopped"
        : result.success
          ? "completed"
          : "failed";
      currentTaskToken = null;
      currentTaskId = null;
      currentTaskText = "";
      // 上报状态（失败时服务端幂等退还额度）。不阻塞 UI。
      if (taskId) {
        try {
          await secureFetch("/api/agent/conversations", {
            method: "PATCH",
            body: JSON.stringify({
              taskId,
              status,
              // 失败时附带错误文本，供服务端日志诊断（如跨页导航后报错）。
              errorMessage: status === "failed" ? (result.data || "") : undefined,
            }),
          });
        } catch {
          // 忽略上报错误
        }
      }
      // 成功完成时累积对话历史，供下一轮 getPageInstructions 注入跨轮上下文。
      // 失败/停止不纳入，避免污染后续理解。
      if (status === "completed") {
        appendConversationTurn(taskText, agent.lastResult?.data || "");
      }
    },
  });

  // ask_user 工具的 resolver（实例属性，非 config）：模型需要向用户澄清时弹出原生 prompt。
  agent.onAskUser = async (question) => {
    return window.prompt(question) || "";
  };

  // 接管彩虹遮罩可见性：仅在执行页面操作工具的瞬间显示光标，其余阶段（思考/数据查询）隐藏。
  // activity 事件由 PageAgentCore 在 executing（tool.execute 前）和 executed（执行后）等时机派发。
  const onActivityForMask = (event: Event) => {
    const detail = (event as CustomEvent).detail as
      | { type?: string; tool?: string }
      | undefined;
    if (!detail) return;
    if (detail.type === "executing" && detail.tool && pageOpTools.has(detail.tool)) {
      showCursorMask();
    } else if (
      detail.type === "executed" ||
      detail.type === "thinking" ||
      detail.type === "error"
    ) {
      hideCursorMask();
    }
  };
  agent.addEventListener("activity", onActivityForMask as EventListener);

  currentWindow.pageAgentCore = agent;
  return agent;
}

interface ConversationStart {
  taskId: string;
  token: string;
  remaining: number;
  limit: number;
  resetsAt: string;
}

/** 停止当前任务（尽力）。 */
export async function stopAgentTask(): Promise<void> {
  try {
    const agent = await getAgent();
    await agent.stop();
  } catch {
    // 忽略
  }
}
